import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A plain modal — no portal/animation library, this app has none. Money and
 * entitlement actions (grant, activate, revert, revoke) all route through
 * this ONE component so a support agent never confirms a real write by
 * misreading an inline button.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel = 'Confirm',
  busy,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
    >
      <div className="w-full max-w-sm rounded-card border border-border bg-surface-field p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-foreground" aria-hidden="true" />
          <div>
            <h2 className="text-h3 font-semibold text-fg-primary">{title}</h2>
            <p className="mt-1 text-body text-fg-secondary">{description}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
